import type { OAuthLoginData } from "@/betby/api/types";
import { startTokenRefreshTimer } from "@/betby/api/http";
import { buildLoginSuccessPayload } from "@/betby/models/auth/authService";
import type { HttpRuntimeAuthSnapshot } from "@/betby/infrastructure/http/httpRuntime";
import { useAuthStore } from "@/betby/store/authStore";
import { useNavStore } from "@/betby/store/navStore";
import { syncAdminToken } from "@/betby/infrastructure/http/authTokenStorage";
import LogUtil from "@/betby/utils/LogUtil";

export interface AuthRuntime {
  getSnapshot?: () => HttpRuntimeAuthSnapshot;
  persistLoginSuccess?: (
    data: OAuthLoginData,
    responseTimestamp?: number,
  ) => void;
  clearAuth?: () => void;
  applyBalances?: (balances: { sc: number; gc: number }) => void;
}

let activeAuthRuntime: AuthRuntime | null = null;

export function registerAuthRuntime(runtime: AuthRuntime | null): void {
  activeAuthRuntime = runtime;
}

export function getAuthRuntime(): AuthRuntime | null {
  return activeAuthRuntime;
}

export function createDefaultAuthRuntime(): AuthRuntime {
  return {
    getSnapshot: () => {
      const state = useAuthStore.getState();
      return {
        accessToken: state.access_token,
        refreshToken: state.refresh_token,
        expiresTime: state.expires_time,
        userId: state.user_id,
        productId: state.product_id,
        businessId: state.business_id,
        userInfo: state.userInfo,
        loginData: state.loginData,
        isAuthenticated: state.isAuthenticated,
      };
    },
    persistLoginSuccess: (data, responseTimestamp) => {
      // buildLoginSuccessPayload 负责把登录响应整理成 store/http 共用的稳定结构。
      const payload = buildLoginSuccessPayload(data);
      const {
        setTokens,
        setUserId,
        setProductId,
        setBusinessId,
        setExpiresTime,
        setLoginData,
      } = useAuthStore.getState();

      setTokens(
        payload.accessToken,
        payload.refreshToken,
        payload.expiresTime ?? undefined,
      );
      syncAdminToken(payload.accessToken);

      if (payload.expiresTime !== null) {
        // refresh timer 需要结合服务端响应时间计算，避免客户端本地时间漂移导致过早/过晚刷新。
        setExpiresTime(payload.expiresTime);
        if (responseTimestamp !== undefined) {
          startTokenRefreshTimer(payload.expiresTime, responseTimestamp);
        }
      }

      if (payload.userId !== null) {
        setUserId(payload.userId);
      }
      if (payload.productId !== null) {
        setProductId(payload.productId);
      }
      if (payload.businessId !== null) {
        setBusinessId(payload.businessId);
      }

      setLoginData(payload.loginData);

      if (payload.currencyMode) {
        useNavStore.getState().initCurrency(payload.currencyMode);
        LogUtil.info(
          "[AuthRuntime] 已根据用户信息更新货币类型:",
          payload.currencyMode,
        );
      }

      LogUtil.info("[AuthRuntime] Token 已保存到 store");
      const { access_token: savedToken } = useAuthStore.getState();
      LogUtil.debug(
        "[AuthRuntime] 验证保存的 token:",
        savedToken ? "存在" : "不存在",
      );
    },
    clearAuth: () => {
      useAuthStore.getState().clearAuth();
      syncAdminToken(null);
    },
    applyBalances: ({ gc, sc }) => {
      const { updateBalance } = useNavStore.getState();
      updateBalance("sc", sc);
      updateBalance("gc", gc);
    },
  };
}
